import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import TableView from '../components/TableView';
import PreviewModal from '../components/PreviewModal';
import EmptyState from '../components/EmptyState';
import { ShareWithTeachersModal } from '../components/ShareWithTeachersModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { EditAssessmentModal } from '../components/EditAssessmentModal';
import { AssignmentsPanel, Assignment } from '../components/AssignmentsPanel';
import { SearchIcon, FolderIcon, FilterIcon, GraduationCapIcon, BookOpenIcon, ArchiveIcon, SlidersHorizontalIcon, ChevronDownIcon } from 'lucide-react';
import { AssessmentStatus } from '../components/StatusBadge';
export function Workspace() {
  const [searchQuery, setSearchQuery] = useState('');
  const [previewAssessment, setPreviewAssessment] = useState(null);
  const [sortBy, setSortBy] = useState<'date-desc' | 'date-asc' | 'title' | 'attempts-desc' | 'attempts-asc'>('date-desc');
  const [statusFilter, setStatusFilter] = useState<AssessmentStatus | 'all'>('all');
  const [gradeFilter, setGradeFilter] = useState<string>('all');
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'my-assessments' | 'saved'>('my-assessments');
  const [shareModalAssessment, setShareModalAssessment] = useState(null);
  const [editModalAssessment, setEditModalAssessment] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    type: 'delete' | 'close' | 'edit';
    assessment: any;
  } | null>(null);
  const [savedAssessmentIds, setSavedAssessmentIds] = useState<Set<number>>(new Set());
  const [assignmentsPanelAssessment, setAssignmentsPanelAssessment] = useState<any>(null);
  const [autoCreateNew, setAutoCreateNew] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Load saved assessments from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('savedAssessments');
    if (saved) {
      setSavedAssessmentIds(new Set(JSON.parse(saved)));
    }
  }, []);
  const handleToggleSave = (assessmentId: number) => {
    setSavedAssessmentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assessmentId)) {
        newSet.delete(assessmentId);
      } else {
        newSet.add(assessmentId);
      }
      // Update localStorage
      localStorage.setItem('savedAssessments', JSON.stringify(Array.from(newSet)));
      return newSet;
    });
  };
  const [workspaceAssessments, setWorkspaceAssessments] = useState([{
    id: 1,
    title: 'Math Fundamentals: Fractions',
    version: 3,
    tags: ['Math', 'Elementary', 'Practice'],
    mode: 'Challenge',
    creator: 'System',
    attempts: 1245,
    uses: 1245,
    icon: '🔢',
    status: 'Ready to share',
    sharedBy: null,
    group: 'Primary (Grades 4-5)',
    subjects: ['Math'],
    customTags: ['Elementary', 'Practice'],
    description: 'Master the basics of fractions including addition, subtraction, multiplication, and division.',
    questions: 15,
    duration: '20 min',
    learningObjectives: 'Students will understand fraction operations, simplify fractions, and convert between fractions and decimals.',
    topicsCovered: 'Addition and subtraction of fractions, multiplication and division, simplification, decimal conversion, and real-world applications.',
    gradeLevel: 'Grades 4-6',
    curriculum: 'Common Core Math Standards 4.NF, 5.NF',
    assignments: [{
      id: 101,
      label: 'Section A — Morning Batch',
      distributionType: 'link',
      status: 'Active',
      responseCount: 18,
      link: 'https://worksheet.example.com/a/abc123'
    }, {
      id: 102,
      label: 'Section B — Afternoon Batch',
      distributionType: 'group',
      status: 'Active',
      responseCount: 6,
      link: 'https://worksheet.example.com/a/xyz789',
      groupName: 'Grade 4B'
    }],
    questionsList: [{
      id: 1,
      text: 'What is 1/2 + 1/4?',
      type: 'Multiple Choice',
      difficulty: 'Easy',
      answer: '3/4'
    }, {
      id: 2,
      text: 'Simplify 8/12 to its lowest terms',
      type: 'Multiple Choice',
      difficulty: 'Easy',
      answer: '2/3'
    }, {
      id: 3,
      text: 'What is 3/4 - 1/3?',
      type: 'Multiple Choice',
      difficulty: 'Medium',
      answer: '5/12'
    }]
  }, {
    id: 2,
    title: 'Science: States of Matter',
    version: 1,
    tags: ['Science', 'Physics'],
    mode: 'Coach',
    creator: 'Jane Smith',
    attempts: 876,
    uses: 876,
    icon: '🧪',
    status: 'Ready to share',
    sharedBy: 'Dr. Sarah Johnson',
    group: 'Middle School (Grades 6-8)',
    subjects: ['Science', 'Physics'],
    customTags: [],
    description: 'Explore the three states of matter and their properties through interactive questions.',
    questions: 12,
    duration: '15 min',
    learningObjectives: 'Students will identify properties of solids, liquids, and gases, and understand phase transitions.',
    topicsCovered: 'States of matter characteristics, particle behavior, phase changes (melting, freezing, evaporation, condensation), and temperature effects.',
    gradeLevel: 'Grades 5-7',
    curriculum: 'NGSS MS-PS1-4, State Science Standards',
    assignments: [],
    questionsList: [{
      id: 1,
      text: 'Name the three main states of matter',
      type: 'Multiple Choice',
      difficulty: 'Easy',
      answer: 'Solid, Liquid, Gas'
    }]
  }, {
    id: 3,
    title: 'English: Grammar Basics',
    version: 2,
    tags: ['English', 'Grammar'],
    mode: 'Challenge',
    creator: 'System',
    attempts: 1543,
    uses: 1543,
    icon: '📝',
    status: 'Ready to share',
    sharedBy: null,
    group: 'Middle School (Grades 6-8)',
    subjects: ['English'],
    customTags: ['Grammar'],
    description: 'Test your knowledge of grammar fundamentals including parts of speech and sentence structure.',
    questions: 20,
    duration: '25 min',
    learningObjectives: 'Students will identify parts of speech, construct proper sentences, and apply grammar rules correctly.',
    topicsCovered: 'Nouns, verbs, adjectives, adverbs, pronouns, prepositions, conjunctions, sentence structure, and punctuation.',
    gradeLevel: 'Grades 6-8',
    curriculum: 'Common Core ELA Standards L.6-8',
    assignments: [{
      id: 301,
      label: 'Homework Assignment',
      distributionType: 'classroom',
      status: 'Active',
      responseCount: 0,
      link: 'https://worksheet.example.com/a/eng101',
      scheduledEnd: 'Tomorrow 5pm'
    }],
    questionsList: []
  }, {
    id: 4,
    title: 'History: Ancient Civilizations',
    version: 1,
    tags: ['History', 'World'],
    mode: 'Coach',
    creator: 'John Doe',
    attempts: 654,
    uses: 654,
    icon: '🏛️',
    status: 'Not yet published',
    sharedBy: 'Prof. Michael Chen',
    group: 'High School (Grades 9-12)',
    subjects: ['History'],
    customTags: ['World'],
    description: 'Journey through ancient civilizations and learn about their cultures and achievements.',
    questions: 18,
    duration: '30 min',
    learningObjectives: 'Students will identify key historical figures, civilizations, and cultural achievements of ancient times.',
    topicsCovered: 'Ancient Mesopotamia, Egypt, Greece, Rome, and other early civilizations, their contributions, and historical significance.',
    gradeLevel: 'Grades 7-9',
    curriculum: 'Common Core History Standards SS.7-9',
    assignments: [],
    questionsList: []
  }, {
    id: 5,
    title: 'Geography: Continents and Oceans',
    version: 4,
    tags: ['Geography'],
    mode: 'Challenge',
    creator: 'System',
    attempts: 987,
    uses: 987,
    icon: '🌍',
    status: 'Ready to share',
    sharedBy: null,
    group: 'Primary (Grades 4-5)',
    subjects: ['Geography'],
    customTags: [],
    description: "Learn about the world's continents and oceans with this comprehensive assessment.",
    questions: 10,
    duration: '15 min',
    learningObjectives: 'Students will identify continents, oceans, and major geographical features, and understand global patterns.',
    topicsCovered: 'Continental and oceanic boundaries, major landforms, climate zones, and global geographical relationships.',
    gradeLevel: 'Grades 4-6',
    curriculum: 'Common Core Geography Standards G.4-6',
    assignments: [{
      id: 501,
      label: 'Pop Quiz',
      distributionType: 'link',
      status: 'Closed',
      responseCount: 24,
      link: 'https://worksheet.example.com/a/geo555'
    }],
    questionsList: []
  }, {
    id: 6,
    title: 'Art History: Renaissance Period',
    version: 1,
    tags: ['Art', 'History', 'Culture', 'Renaissance'],
    mode: 'Coach',
    creator: 'Sarah Johnson',
    attempts: 432,
    uses: 432,
    icon: '🎨',
    status: 'Archived',
    sharedBy: null,
    group: 'High School (Grades 9-12)',
    subjects: ['Art', 'History'],
    customTags: ['Culture', 'Renaissance'],
    description: 'Discover the masterpieces and artists of the Renaissance period.',
    questions: 14,
    duration: '20 min',
    learningObjectives: 'Students will identify key Renaissance artists, artworks, and understand the cultural and artistic movements of the period.',
    topicsCovered: 'Renaissance art characteristics, major artists (Leonardo, Michelangelo, Raphael), famous works, and the humanist philosophy that influenced the era.',
    gradeLevel: 'Grades 8-10',
    curriculum: 'Common Core Art History Standards AH.8-10',
    assignments: [],
    questionsList: []
  }, {
    id: 7,
    title: 'Music Theory: Basics',
    version: 2,
    tags: ['Music'],
    mode: 'Challenge',
    creator: 'System',
    attempts: 765,
    uses: 765,
    icon: '🎵',
    status: 'Ready to share',
    sharedBy: null,
    group: 'Middle School (Grades 6-8)',
    subjects: ['Music'],
    customTags: [],
    description: 'Learn the fundamentals of music theory including notes, scales, and rhythms.',
    questions: 16,
    duration: '20 min',
    learningObjectives: 'Students will understand musical notation, scales, intervals, and basic rhythm patterns.',
    topicsCovered: 'Musical notes, scales, intervals, time signatures, rhythm, and basic music theory concepts.',
    gradeLevel: 'Grades 5-7',
    curriculum: 'Common Core Music Standards M.5-7',
    assignments: [{
      id: 701,
      label: 'Practice Session',
      distributionType: 'link',
      status: 'Active',
      responseCount: 12,
      link: 'https://worksheet.example.com/a/mus123'
    }],
    questionsList: []
  }, {
    id: 8,
    title: 'Computer Science: Algorithms',
    version: 1,
    tags: ['Computer Science', 'Programming'],
    mode: 'Coach',
    creator: 'Michael Chen',
    attempts: 543,
    uses: 543,
    icon: '💻',
    status: 'Ready to share',
    sharedBy: null,
    group: 'High School (Grades 9-12)',
    subjects: ['Computer Science'],
    customTags: ['Programming'],
    description: 'Understand basic algorithms and their applications in computer science.',
    questions: 12,
    duration: '25 min',
    learningObjectives: 'Students will understand algorithmic thinking, basic programming concepts, and problem-solving strategies.',
    topicsCovered: 'Algorithms, pseudocode, loops, conditionals, data structures, and problem-solving approaches.',
    gradeLevel: 'Grades 9-12',
    curriculum: 'Computer Science Principles (CS1)',
    assignments: [],
    questionsList: []
  }]);
  // Get saved assessments (filter workspaceAssessments by savedAssessmentIds)
  const savedAssessments = workspaceAssessments.filter(assessment => savedAssessmentIds.has(assessment.id));
  // Extract unique grades and subjects from assessments
  const allGrades = Array.from(new Set(workspaceAssessments.map(a => a.group).filter(Boolean))).sort();
  const allSubjects = Array.from(new Set(workspaceAssessments.flatMap(a => a.subjects || []).filter(Boolean))).sort();
  // Determine which assessments to show — always my-assessments now
  const assessmentsToDisplay = workspaceAssessments;
  // Filter and sort assessments
  const filteredAssessments = assessmentsToDisplay.filter(assessment => {
    // Hide archived unless showArchived is true
    if (!showArchived && assessment.status === 'Archived') {
      return false;
    }
    // Status filter (only for My Assessments tab) — skip 'all' and 'Archived' (handled by toggle)
    if (activeTab === 'my-assessments' && statusFilter !== 'all' && statusFilter !== 'Archived' && assessment.status !== statusFilter) {
      return false;
    }
    // Grade filter
    if (gradeFilter !== 'all' && assessment.group !== gradeFilter) {
      return false;
    }
    // Subject filter
    if (subjectFilter !== 'all' && !assessment.subjects?.includes(subjectFilter)) {
      return false;
    }
    // Search filter
    if (searchQuery === '') return true;
    return assessment.title.toLowerCase().includes(searchQuery.toLowerCase()) || assessment.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())) || assessment.sharedBy && assessment.sharedBy.toLowerCase().includes(searchQuery.toLowerCase());
  }).sort((a, b) => {
    if (sortBy === 'date-desc') {
      return b.id - a.id;
    } else if (sortBy === 'date-asc') {
      return a.id - b.id;
    } else if (sortBy === 'title') {
      return a.title.localeCompare(b.title);
    } else if (sortBy === 'attempts-desc') {
      return b.attempts - a.attempts;
    } else if (sortBy === 'attempts-asc') {
      return a.attempts - b.attempts;
    }
    return 0;
  });
  const handleShare = (emails: string[], message: string) => {
    console.log('Sharing assessment:', shareModalAssessment?.title);
    console.log('To emails:', emails);
    console.log('Message:', message);
    // TODO: Implement actual sharing logic
    setShareModalAssessment(null);
  };
  const handleClone = (assessment: any) => {
    // Create a cloned assessment with new ID and "Not yet published" status
    const maxId = Math.max(...workspaceAssessments.map(a => a.id));
    const clonedAssessment = {
      ...assessment,
      id: maxId + 1,
      title: `${assessment.title} (Copy)`,
      status: 'Not yet published',
      sharedBy: null,
      attempts: 0,
      assignments: []
    };
    // Find the index of the original assessment
    const originalIndex = workspaceAssessments.findIndex(a => a.id === assessment.id);
    // Insert the cloned assessment right after the original
    const newAssessments = [...workspaceAssessments];
    newAssessments.splice(originalIndex + 1, 0, clonedAssessment);
    setWorkspaceAssessments(newAssessments);
  };
  const handleDelete = (assessment: any) => {
    setWorkspaceAssessments(prev => prev.filter(a => a.id !== assessment.id));
  };
  const handleClose = (assessment: any) => {
    setWorkspaceAssessments(prev => prev.map(a => a.id === assessment.id ? {
      ...a,
      status: 'Access closed'
    } : a));
  };
  const handleEdit = (assessment: any) => {
    setEditModalAssessment(assessment);
  };
  const handleSaveEdit = (data: {
    title: string;
    group: string;
    subjects: string[];
    tags: string[];
  }) => {
    setWorkspaceAssessments(prev => prev.map(a => a.id === editModalAssessment.id ? {
      ...a,
      title: data.title,
      group: data.group,
      subjects: data.subjects,
      customTags: data.tags,
      tags: [...data.subjects, ...data.tags]
    } : a));
    setEditModalAssessment(null);
  };
  const handleUpdateAssignments = (assessmentId: number, assignments: Assignment[]) => {
    setWorkspaceAssessments(prev => prev.map(a => a.id === assessmentId ? {
      ...a,
      assignments
    } : a));
    // Also update the panel state to reflect changes immediately
    setAssignmentsPanelAssessment((prev: any) => prev && prev.id === assessmentId ? {
      ...prev,
      assignments
    } : prev);
  };
  const handleAction = (action: string, assessment: any) => {
    switch (action) {
      case 'Clone':
      case 'Duplicate':
        // Handle Duplicate alias
        handleClone(assessment);
        break;
      case 'Delete':
        handleDelete(assessment);
        break;
      case 'Archive':
        setWorkspaceAssessments(prev => prev.map(a => a.id === assessment.id ? {
          ...a,
          status: 'Archived'
        } : a));
        break;
      case 'Restore':
        // Handle Restore
        setWorkspaceAssessments(prev => prev.map(a => a.id === assessment.id ? {
          ...a,
          status: 'Ready to share'
        } : a));
        break;
      case 'Revert to Draft':
        // Handle Revert
        setWorkspaceAssessments(prev => prev.map(a => a.id === assessment.id ? {
          ...a,
          status: 'Not yet published'
        } : a));
        break;
      case 'Edit':
        handleEdit(assessment);
        break;
      case 'Settings':
        // Handle Settings (maps to Edit for now)
        handleEdit(assessment);
        break;
      case 'Manage Labels':
        handleEdit(assessment);
        break;
      case 'Publish':
        setWorkspaceAssessments(prev => prev.map(a => a.id === assessment.id ? {
          ...a,
          status: 'Ready to share'
        } : a));
        break;
      default:
        console.log(`Action: ${action}`, assessment.title);
    }
  };
  const handleQuickShare = (assessment: any) => {
    setAssignmentsPanelAssessment(assessment);
    setAutoCreateNew(true);
  };
  const getConfirmationContent = () => {
    if (!confirmationModal) return {
      title: '',
      description: '',
      confirmLabel: ''
    };
    const {
      type,
      assessment
    } = confirmationModal;
    switch (type) {
      case 'delete':
        return {
          title: 'Delete Active Assessment?',
          description: 'This assessment is currently active. Students may be working on it. Are you sure you want to delete it?',
          confirmLabel: 'Delete Assessment'
        };
      case 'close':
        return {
          title: 'Close Assessment Access?',
          description: 'Students will no longer be able to access or complete this assessment. You can reopen it later if needed.',
          confirmLabel: 'Close Access'
        };
      case 'edit':
        return {
          title: 'Edit Active Assessment?',
          description: 'This assessment is currently active. Editing it may affect students who are currently taking it. Consider creating a new version instead.',
          confirmLabel: 'Continue Editing'
        };
      default:
        return {
          title: '',
          description: '',
          confirmLabel: ''
        };
    }
  };
  return <div className="flex flex-col min-h-screen bg-background" data-id="element-3808">
      <Header data-id="element-3809" />
      <div className="flex flex-1 overflow-hidden" data-id="element-3810">
        <div className="hidden md:block" data-id="element-3811">
          <Sidebar data-id="element-3812" />
        </div>
        <div className="flex-1 flex flex-col min-w-0 w-full overflow-y-auto" data-id="element-3813">
          <main className="flex-1" data-id="element-3814">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-7" data-id="element-3815">
              <section data-id="element-3816">
                {/* Header */}
                <div className="flex items-center justify-between mb-5" data-id="element-3817">
                  <div className="flex items-center gap-3" data-id="element-3818">
                    <div className="w-9 h-9 bg-[#00B75F]/10 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-3819">
                      <FolderIcon size={18} className="text-[#00B75F]" data-id="element-3820" />
                    </div>
                    <div data-id="element-3821">
                      <h2 className="text-lg sm:text-xl font-bold text-text-primary leading-tight" data-id="element-3822">
                        Workspace
                      </h2>
                      <p className="text-xs text-text-tertiary hidden sm:block" data-id="element-3823">
                        Browse and manage your assessment library
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-text-tertiary bg-surface border border-custom px-2.5 py-1 rounded-full" data-id="element-3824">
                    {filteredAssessments.length} / {assessmentsToDisplay.length}
                  </span>
                </div>

                {/* Search + Filter Toggle Row */}
                <div className="flex items-center gap-2 mb-3" data-id="element-3825">
                  {/* Search */}
                  <div className="relative flex-1" data-id="element-3826">
                    <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-3827" />
                    <input type="text" placeholder="Search assessments..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-8 pr-4 py-1.5 bg-surface border border-custom rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-primary placeholder-text-tertiary" data-id="element-3828" />
                  </div>

                  {/* Filter toggle — mobile only; desktop always shows filters */}
                  <button onClick={() => setShowFilters(!showFilters)} className={`sm:hidden flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${showFilters || statusFilter !== 'all' || gradeFilter !== 'all' || subjectFilter !== 'all' ? 'bg-[#00B75F]/10 border-[#00B75F]/30 text-[#00B75F]' : 'bg-surface border-custom text-text-secondary'}`} data-id="element-3829">
                    <FilterIcon size={12} data-id="element-3830" />
                    Filters
                    {(statusFilter !== 'all' || gradeFilter !== 'all' || subjectFilter !== 'all') && <span className="w-1.5 h-1.5 rounded-full bg-[#00B75F] ml-0.5" data-id="element-3831" />}
                  </button>
                </div>

                {/* Filter Row — always visible on sm+, collapsible on mobile */}
                <AnimatePresence data-id="element-3832">
                  {(showFilters || typeof window !== 'undefined') && <div className={`${showFilters ? 'block' : 'hidden'} sm:block`} data-id="element-3833">
                      <div className="flex items-center gap-2 flex-wrap mb-3" data-id="element-3834">
                        {/* Status Filter */}
                        <div className="relative" data-id="element-3835">
                          <FilterIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-3836" />
                          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as AssessmentStatus | 'all')} className="pl-6 pr-5 py-1.5 bg-surface border border-custom rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-secondary appearance-none cursor-pointer hover:border-gray-300 transition-colors" data-id="element-3837">
                            <option value="all" data-id="element-3838">All Statuses</option>
                            <option value="Not yet published" data-id="element-3839">Draft</option>
                            <option value="Ready to share" data-id="element-3840">
                              Ready to share
                            </option>
                          </select>
                        </div>

                        {/* Grade Filter */}
                        <div className="relative" data-id="element-3841">
                          <GraduationCapIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-3842" />
                          <select value={gradeFilter} onChange={e => setGradeFilter(e.target.value)} className="pl-6 pr-5 py-1.5 bg-surface border border-custom rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-secondary appearance-none cursor-pointer hover:border-gray-300 transition-colors" data-id="element-3843">
                            <option value="all" data-id="element-3844">All Grades</option>
                            {allGrades.map(grade => <option key={grade} value={grade} data-id="element-3845">
                                {grade}
                              </option>)}
                          </select>
                        </div>

                        {/* Subject Filter */}
                        <div className="relative" data-id="element-3846">
                          <BookOpenIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-3847" />
                          <select value={subjectFilter} onChange={e => setSubjectFilter(e.target.value)} className="pl-6 pr-5 py-1.5 bg-surface border border-custom rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-secondary appearance-none cursor-pointer hover:border-gray-300 transition-colors" data-id="element-3848">
                            <option value="all" data-id="element-3849">All Subjects</option>
                            {allSubjects.map(subject => <option key={subject} value={subject} data-id="element-3850">
                                {subject}
                              </option>)}
                          </select>
                        </div>

                        {/* Sort */}
                        <div className="relative" data-id="element-3851">
                          <SlidersHorizontalIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-3852" />
                          <select value={sortBy} onChange={e => setSortBy(e.target.value as 'date-desc' | 'date-asc' | 'title' | 'attempts-desc' | 'attempts-asc')} className="pl-6 pr-5 py-1.5 bg-surface border border-custom rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-secondary appearance-none cursor-pointer hover:border-gray-300 transition-colors" data-id="element-3853">
                            <option value="date-desc" data-id="element-3854">Most Recent</option>
                            <option value="date-asc" data-id="element-3855">Oldest First</option>
                            <option value="title" data-id="element-3856">Title A-Z</option>
                            <option value="attempts-desc" data-id="element-3857">Most Attempts</option>
                            <option value="attempts-asc" data-id="element-3858">Least Attempts</option>
                          </select>
                        </div>

                        {/* Archive toggle */}
                        <button onClick={() => setShowArchived(!showArchived)} title={showArchived ? 'Hide archived' : 'Show archived'} className={`relative flex items-center justify-center w-6 h-6 rounded-md border transition-all ${showArchived ? 'bg-[#00B75F]/10 border-[#00B75F]/30 text-[#00B75F]' : 'bg-surface border-custom text-text-tertiary hover:text-text-secondary hover:border-gray-300'}`} data-id="element-3859">
                          <ArchiveIcon size={12} data-id="element-3860" />
                          {showArchived && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#00B75F]" data-id="element-3861" />}
                        </button>

                        {/* Active filter chips */}
                        {(statusFilter !== 'all' || gradeFilter !== 'all' || subjectFilter !== 'all') && <>
                            {statusFilter !== 'all' && <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#00B75F]/10 text-[#00B75F] text-xs font-medium rounded-lg" data-id="element-3862">
                                {statusFilter === 'Not yet published' ? 'Draft' : statusFilter}
                                <button onClick={() => setStatusFilter('all')} className="hover:text-[#00a055] ml-0.5 leading-none" data-id="element-3863">
                                  ×
                                </button>
                              </span>}
                            {gradeFilter !== 'all' && <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium rounded-lg" data-id="element-3864">
                                {gradeFilter}
                                <button onClick={() => setGradeFilter('all')} className="hover:text-[#1d4ed8] ml-0.5 leading-none" data-id="element-3865">
                                  ×
                                </button>
                              </span>}
                            {subjectFilter !== 'all' && <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#FF9D33]/10 text-[#FF9D33] text-xs font-medium rounded-lg" data-id="element-3866">
                                {subjectFilter}
                                <button onClick={() => setSubjectFilter('all')} className="hover:text-[#e68a2e] ml-0.5 leading-none" data-id="element-3867">
                                  ×
                                </button>
                              </span>}
                            <button onClick={() => {
                        setStatusFilter('all');
                        setGradeFilter('all');
                        setSubjectFilter('all');
                      }} className="text-xs text-text-tertiary hover:text-text-secondary underline" data-id="element-3868">
                              Clear
                            </button>
                          </>}
                      </div>
                    </div>}
                </AnimatePresence>

                {/* Results Count */}
                <div className="mb-3" data-id="element-3869">
                  <p className="text-xs text-text-tertiary" data-id="element-3870">
                    {filteredAssessments.length === assessmentsToDisplay.length ? `${assessmentsToDisplay.length} assessments` : `${filteredAssessments.length} of ${assessmentsToDisplay.length} assessments`}
                  </p>
                </div>

                {/* Table View */}
                {filteredAssessments.length === 0 ? <EmptyState icon={<FolderIcon size={48} className="text-text-tertiary" data-id="element-3872" />} title="No assessments found" description="Try adjusting your search or filters" actionLabel="Clear Filters" onAction={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setGradeFilter('all');
                setSubjectFilter('all');
              }} data-id="element-3871" /> : <TableView assessments={filteredAssessments} onPreview={setPreviewAssessment} onShareWithTeachers={setShareModalAssessment} onAction={handleAction} onOpenAssignments={setAssignmentsPanelAssessment} onQuickShare={handleQuickShare} data-id="element-3873" />}
              </section>
            </div>
          </main>
        </div>
      </div>

      {/* Assignments Panel */}
      <AssignmentsPanel isOpen={!!assignmentsPanelAssessment} onClose={() => {
      setAssignmentsPanelAssessment(null);
      setAutoCreateNew(false);
    }} assessment={assignmentsPanelAssessment} onUpdateAssignments={handleUpdateAssignments} autoCreateNew={autoCreateNew} data-id="element-3874" />

      {/* Preview Modal */}
      {previewAssessment && <PreviewModal assessment={previewAssessment} onClose={() => setPreviewAssessment(null)} userRole="teacher" isSaved={savedAssessmentIds.has(previewAssessment.id)} onToggleSave={handleToggleSave} assignments={previewAssessment?.assignments || []} onOpenAssignmentsPanel={autoCreate => {
      setAssignmentsPanelAssessment(previewAssessment);
      setAutoCreateNew(autoCreate);
      setPreviewAssessment(null);
    }} data-id="element-3875" />}

      {/* Share with Teachers Modal */}
      {shareModalAssessment && <ShareWithTeachersModal assessment={shareModalAssessment} onClose={() => setShareModalAssessment(null)} onShare={handleShare} data-id="element-3876" />}

      {/* Edit Assessment Modal */}
      {editModalAssessment && <EditAssessmentModal isOpen={!!editModalAssessment} onClose={() => setEditModalAssessment(null)} onSave={handleSaveEdit} assessment={editModalAssessment} data-id="element-3877" />}

      {/* Confirmation Modal */}
      {confirmationModal && <ConfirmationModal isOpen={confirmationModal.isOpen} onClose={() => setConfirmationModal(null)} onConfirm={() => {
      const {
        type,
        assessment
      } = confirmationModal;
      switch (type) {
        case 'delete':
          handleDelete(assessment);
          break;
        case 'close':
          handleClose(assessment);
          break;
        case 'edit':
          handleEdit(assessment);
          break;
      }
    }} title={getConfirmationContent().title} description={getConfirmationContent().description} confirmLabel={getConfirmationContent().confirmLabel} type={confirmationModal.type} assessmentTitle={confirmationModal.assessment.title} data-id="element-3878" />}
    </div>;
}